import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { PropertySearch, PropertySearchRow } from '@metron/shared';
import {
  parsePropertySearchParams,
  SEARCH_DEFAULT_PAGE_SIZE,
  toPropertySearchParams,
} from '@metron/shared';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { PropertySearchFilters } from '../components/property-search/PropertySearchFilters.tsx';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { usePropertyFilter } from '../hooks/usePropertyFilter.ts';
import { useCustomers } from '../hooks/useCustomers.ts';

// Column ids match the spec's sortBy values, so useTableUrlState's `sortBy`
// param is directly the spec's. Metron ID isn't a server sort key.
const columns: ColumnDef<PropertySearchRow>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Property' />,
    cell: ({ row }) => <div className='font-medium'>{row.original.name}</div>,
  },
  {
    accessorKey: 'metronId',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title='Metron ID' />,
    cell: ({ row }) => row.original.metronId,
  },
  {
    accessorKey: 'customerName',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Customer' />,
    cell: ({ row }) =>
      row.original.customerName ?? <span className='text-muted-foreground'>N/A</span>,
  },
  {
    accessorKey: 'meterCount',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Meters' />,
    cell: ({ row }) => <div className='text-right'>{row.original.meterCount.toLocaleString()}</div>,
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Created' />,
    cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
  },
];

/**
 * Property Search (Smart Search results, MET-3526)
 *
 * The property counterpart of Meter and Customer Search: the whole search
 * lives in the URL, filters render as editable chips, and paging/sorting use
 * the shared URL-backed table state under the spec's own keys. There's no
 * property detail page, so a row opens its customer's property list.
 */
export function PropertySearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { customers, isLoading: customersLoading } = useCustomers();
  const { pagination, sorting, onPaginationChange, onSortingChange } = useTableUrlState({
    defaultPageSize: SEARCH_DEFAULT_PAGE_SIZE,
  });

  const parsed = useMemo(() => parsePropertySearchParams(searchParams), [searchParams]);
  const spec = parsed.success ? parsed.data : null;
  const { data, isLoading, isFetching, error } = usePropertyFilter(spec);

  const setSpec = (next: PropertySearch) => setSearchParams(toPropertySearchParams(next));

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold tracking-tight'>Property Search</h1>
        <p className='text-muted-foreground'>
          Filter properties by customer, name, and meter count. Share the URL to share the search.
        </p>
      </div>

      {!parsed.success
        ? (
          <Card>
            <CardHeader>
              <CardTitle>This search link isn't valid</CardTitle>
              <CardDescription>
                {parsed.error.issues.map((i) => `${i.path.join('.') || 'search'}: ${i.message}`)
                  .join('; ')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link className='underline' to='/search/properties'>Start a new search</Link>
            </CardContent>
          </Card>
        )
        : (
          <Card>
            <CardHeader className='space-y-4'>
              <PropertySearchFilters
                spec={parsed.data}
                customers={customers}
                customersLoading={customersLoading}
                onChange={setSpec}
              />
              <CardDescription aria-live='polite'>
                {isLoading
                  ? 'Searching…'
                  : error
                  ? null
                  : `${(data?.total ?? 0).toLocaleString()} propert${
                    data?.total === 1 ? 'y' : 'ies'
                  }${isFetching ? ' · updating…' : ''}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error
                ? (
                  <p role='alert' className='text-sm text-destructive py-4 text-center'>
                    Search failed: {error instanceof Error ? error.message : 'unknown error'}
                  </p>
                )
                : (
                  <DataTable
                    columns={columns}
                    data={data?.properties ?? []}
                    onRowClick={(property) =>
                      navigate(`/customers/${property.customerId}?tab=properties`)}
                    manualPagination
                    rowCount={data?.total ?? 0}
                    pagination={pagination}
                    onPaginationChange={onPaginationChange}
                    manualSorting
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    emptyMessage={isLoading ? 'Searching…' : 'No properties match these filters.'}
                  />
                )}
            </CardContent>
          </Card>
        )}
    </div>
  );
}
